import { defineStore } from 'pinia'
import userApi from '@src/api/user'
import { IEmailNotificationChannelSettings, INotificationChannelSettings } from '@src/types/notifications.types'
import { ref } from 'vue'

export const useCommunicationSettingsStore = defineStore('communicationSettings', () => {
  const loading = ref(true)
  const updating = ref(false)
  const error = ref(false)
  const emailSettings = ref<IEmailNotificationChannelSettings | undefined>(undefined)
  const webPushSettings = ref<INotificationChannelSettings | undefined>(undefined)

  const loadCommunicationSettings = async (force = false): Promise<void> => {
    if (!emailSettings.value || !webPushSettings.value || force) {
      loading.value = true
      error.value = false

      try {
        const { data: fetchedEmailSettings } = await userApi.getEmailSettings()
        const { data: fetchedWebPushSettings } = await userApi.getWebPushSettings()
        emailSettings.value = fetchedEmailSettings
        webPushSettings.value = fetchedWebPushSettings
      } catch {
        error.value = true
      } finally {
        loading.value = false
      }
    }
  }

  const updateEmailSettings = async (settings: IEmailNotificationChannelSettings): Promise<void> => {
    updating.value = true
    try {
      const { data: updatedEmailSettings } = await userApi.setEmailSettings(settings)
      emailSettings.value = updatedEmailSettings
    } finally {
      updating.value = false
    }
  }

  const updateWebPushSettings = async (settings: INotificationChannelSettings): Promise<void> => {
    updating.value = true
    try {
      const { data: updatedWebPushSettings } = await userApi.setWebPushSettings(settings)
      webPushSettings.value = updatedWebPushSettings
    } finally {
      updating.value = false
    }
  }

  return {
    loading,
    updating,
    error,
    emailSettings,
    webPushSettings,
    loadCommunicationSettings,
    updateEmailSettings,
    updateWebPushSettings
  }
})
